/**
 * Universal CSV Parser for importing card collections from various sources.
 * Supports auto-detection of formats including TradeBinder, Deckbox, and generic CSV.
 */

import { ParsedImportCard } from './importSessionStorage';

export type SupportedFormat = 'tradebinder' | 'deckbox' | 'tcgplayer' | 'moxfield' | 'generic';

export interface FormatDetectionResult {
  delimiter: ',' | ';' | '\t';
  format: SupportedFormat;
  hasScryfallId: boolean;
  columns: string[];
  totalRows: number;
}

export interface ParseResult {
  cards: ParsedImportCard[];
  format: FormatDetectionResult;
  skippedRows: number;
  errors: Array<{ row: number; error: string }>;
}

/**
 * Safely decode base64 data from URL parameter.
 * Handles URL-safe base64 variants (- and _ instead of + and /).
 */
export function safeBase64Decode(encoded: string): string {
  try {
    // Handle URL-safe base64 (RFC 4648)
    const sanitized = encoded.replace(/-/g, '+').replace(/_/g, '/');

    // Add padding if needed
    const remainder = sanitized.length % 4;
    const padded = remainder ? sanitized + '='.repeat(4 - remainder) : sanitized;

    return atob(padded);
  } catch (error) {
    throw new Error('Invalid import data: Unable to decode base64');
  }
}

/**
 * Detect the CSV format by analyzing the header row.
 */
export function detectFormat(csvText: string): FormatDetectionResult {
  const lines = csvText.split(/\r?\n/).filter((line) => line.trim());
  if (lines.length === 0) {
    throw new Error('No data found in import');
  }

  const firstLine = lines[0];

  // Check for semicolon delimiter (TradeBinder)
  const hasSemicolon = firstLine.includes(';');
  const hasComma = firstLine.includes(',');
  const hasTab = firstLine.includes('\t');

  let delimiter: ',' | ';' | '\t' = ',';
  if (hasSemicolon && !hasComma) {
    delimiter = ';';
  } else if (hasTab && !hasComma && !hasSemicolon) {
    delimiter = '\t';
  }

  const columns = parseCSVLine(firstLine, delimiter).map((col) => col.toLowerCase().trim());

  // TradeBinder detection: semicolon-delimited with scryfall_uuid column
  if (delimiter === ';' && columns.includes('scryfall_uuid')) {
    return {
      delimiter,
      format: 'tradebinder',
      hasScryfallId: true,
      columns,
      totalRows: lines.length - 1,
    };
  }

  // Deckbox detection: has "tradelist count" column
  if (columns.some((col) => col.includes('tradelist count'))) {
    return {
      delimiter,
      format: 'deckbox',
      hasScryfallId: false,
      columns,
      totalRows: lines.length - 1,
    };
  }

  // TCGPlayer detection: has "tcgplayer id" column
  if (columns.some((col) => col.includes('tcgplayer'))) {
    return {
      delimiter,
      format: 'tcgplayer',
      hasScryfallId: false,
      columns,
      totalRows: lines.length - 1,
    };
  }

  // Moxfield detection: has specific moxfield columns
  if (columns.includes('scryfall id') || columns.some((col) => col.includes('moxfield'))) {
    return {
      delimiter,
      format: 'moxfield',
      hasScryfallId: columns.includes('scryfall id'),
      columns,
      totalRows: lines.length - 1,
    };
  }

  // Generic fallback
  return {
    delimiter,
    format: 'generic',
    hasScryfallId: columns.includes('scryfall_uuid') || columns.includes('scryfall id'),
    columns,
    totalRows: lines.length - 1,
  };
}

/**
 * Parse a single CSV line, handling quoted values.
 */
function parseCSVLine(line: string, delimiter: string = ','): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    const nextChar = line[i + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        // Escaped quote
        current += '"';
        i++; // Skip next quote
      } else {
        // Toggle quote state
        inQuotes = !inQuotes;
      }
    } else if (char === delimiter && !inQuotes) {
      // End of field
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }

  // Add last field
  result.push(current.trim());

  return result;
}

/**
 * Parse TradeBinder CSV format.
 * Columns: export_type;scryfall_uuid;set_code;quantity;foil_quantity;card_name;set_name;cardMarketId;english_card_name;lang;collector_number
 */
function parseTradeBinder(csvText: string, columns: string[]): ParseResult {
  const lines = csvText.split(/\r?\n/).filter((line) => line.trim());
  const cards: ParsedImportCard[] = [];
  const errors: Array<{ row: number; error: string }> = [];
  let skippedRows = 0;

  // Find column indices
  const exportTypeIdx = columns.indexOf('export_type');
  const scryfallUuidIdx = columns.indexOf('scryfall_uuid');
  const setCodeIdx = columns.indexOf('set_code');
  const quantityIdx = columns.indexOf('quantity');
  const foilQuantityIdx = columns.indexOf('foil_quantity');
  const cardNameIdx = columns.indexOf('card_name');
  const langIdx = columns.indexOf('lang');

  // Validate required columns
  if (scryfallUuidIdx === -1) {
    throw new Error('TradeBinder CSV missing required scryfall_uuid column');
  }

  // Skip header row
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;

    const values = parseCSVLine(line, ';');

    // Filter: only process export_type === 'card' (skip 'deck')
    const exportType = exportTypeIdx !== -1 ? values[exportTypeIdx]?.toLowerCase() : 'card';
    if (exportType === 'deck') {
      skippedRows++;
      continue;
    }

    const scryfallUuid = values[scryfallUuidIdx]?.trim();
    if (!scryfallUuid) {
      errors.push({ row: i, error: 'Missing Scryfall UUID' });
      continue;
    }

    // Validate UUID format (basic check)
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!uuidRegex.test(scryfallUuid)) {
      errors.push({ row: i, error: `Invalid UUID format: ${scryfallUuid}` });
      continue;
    }

    const cardName = cardNameIdx !== -1 ? values[cardNameIdx]?.trim() : '';
    const setCode = setCodeIdx !== -1 ? values[setCodeIdx]?.trim() : undefined;
    const quantity = quantityIdx !== -1 ? parseInt(values[quantityIdx], 10) || 0 : 0;
    const foilQuantity = foilQuantityIdx !== -1 ? parseInt(values[foilQuantityIdx], 10) || 0 : 0;
    const language = langIdx !== -1 ? values[langIdx]?.trim() : undefined;

    // Skip if no quantity
    if (quantity === 0 && foilQuantity === 0) {
      skippedRows++;
      continue;
    }

    cards.push({
      scryfallUuid,
      cardName: cardName || 'Unknown Card',
      setCode,
      quantity,
      foilQuantity,
      language,
      originalRow: i,
    });
  }

  return {
    cards,
    format: {
      delimiter: ';',
      format: 'tradebinder',
      hasScryfallId: true,
      columns,
      totalRows: lines.length - 1,
    },
    skippedRows,
    errors,
  };
}

/**
 * Parse generic CSV format (fallback for unsupported formats).
 * Attempts to find common column names.
 */
function parseGeneric(
  csvText: string,
  columns: string[],
  format: FormatDetectionResult
): ParseResult {
  const lines = csvText.split(/\r?\n/).filter((line) => line.trim());
  const cards: ParsedImportCard[] = [];
  const errors: Array<{ row: number; error: string }> = [];
  let skippedRows = 0;

  // Find column indices - flexible matching
  const scryfallUuidIdx = columns.findIndex(
    (col) => col.includes('scryfall') && (col.includes('uuid') || col.includes('id'))
  );
  const cardNameIdx = columns.findIndex((col) => col.includes('name') || col.includes('card'));
  const setCodeIdx = columns.findIndex((col) => col === 'set' || col.includes('set_code') || col.includes('edition'));
  const quantityIdx = columns.findIndex(
    (col) => col.includes('quantity') || col.includes('qty') || col.includes('count')
  );
  const foilIdx = columns.findIndex((col) => col.includes('foil'));
  const langIdx = columns.findIndex((col) => col.includes('lang'));

  // Must have either Scryfall UUID or card name
  if (scryfallUuidIdx === -1 && cardNameIdx === -1) {
    throw new Error('CSV must contain either a Scryfall UUID or Card Name column');
  }

  // For non-Scryfall formats, we can't import (need UUID for backend)
  if (scryfallUuidIdx === -1) {
    throw new Error(
      `${format.format} format is not yet supported. TradeMagic requires Scryfall UUIDs for card matching.`
    );
  }

  // Skip header row
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;

    const values = parseCSVLine(line, format.delimiter);

    const scryfallUuid = values[scryfallUuidIdx]?.trim();
    if (!scryfallUuid) {
      errors.push({ row: i, error: 'Missing Scryfall UUID' });
      continue;
    }

    const cardName = cardNameIdx !== -1 ? values[cardNameIdx]?.trim() : '';
    const setCode = setCodeIdx !== -1 ? values[setCodeIdx]?.trim() : undefined;
    const language = langIdx !== -1 ? values[langIdx]?.trim() : undefined;

    // Parse quantity - default to 1 if not found
    let quantity = 1;
    let foilQuantity = 0;

    if (quantityIdx !== -1) {
      quantity = parseInt(values[quantityIdx], 10) || 1;
    }

    // Check for foil column
    if (foilIdx !== -1) {
      const foilValue = values[foilIdx]?.toLowerCase().trim();
      const isFoil = foilValue === 'true' || foilValue === 'yes' || foilValue === '1' || foilValue === 'foil';
      if (isFoil) {
        foilQuantity = quantity;
        quantity = 0;
      }
    }

    cards.push({
      scryfallUuid,
      cardName: cardName || 'Unknown Card',
      setCode,
      quantity,
      foilQuantity,
      language,
      originalRow: i,
    });
  }

  return {
    cards,
    format,
    skippedRows,
    errors,
  };
}

/**
 * Main parsing function - auto-detects format and parses accordingly.
 */
export function parseUniversalCsv(csvText: string): ParseResult {
  if (!csvText || csvText.trim().length === 0) {
    throw new Error('No data provided');
  }

  const format = detectFormat(csvText);
  const lines = csvText.split(/\r?\n/).filter((line) => line.trim());

  // Validate card count
  if (lines.length < 2) {
    throw new Error('No card data found. Make sure to export your collection, not just decks.');
  }

  // Parse based on detected format
  switch (format.format) {
    case 'tradebinder':
      return parseTradeBinder(csvText, format.columns);

    case 'deckbox':
    case 'tcgplayer':
    case 'moxfield':
    case 'generic':
    default:
      return parseGeneric(csvText, format.columns, format);
  }
}

/**
 * Validate parsed cards before import.
 */
export function validateParsedCards(cards: ParsedImportCard[]): {
  valid: boolean;
  errors: string[];
} {
  const errors: string[] = [];

  if (cards.length === 0) {
    errors.push('No valid cards found to import');
  }

  // Check for cards without valid quantities
  const zeroQuantityCount = cards.filter((c) => c.quantity === 0 && c.foilQuantity === 0).length;
  if (zeroQuantityCount > 0) {
    errors.push(`${zeroQuantityCount} cards have zero quantity`);
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * Calculate import summary statistics.
 */
export function calculateImportSummary(cards: ParsedImportCard[]): {
  totalCards: number;
  uniqueCards: number;
  totalNonFoil: number;
  totalFoil: number;
} {
  const uniqueUuids = new Set(cards.map((c) => c.scryfallUuid));

  return {
    totalCards: cards.reduce((sum, c) => sum + c.quantity + c.foilQuantity, 0),
    uniqueCards: uniqueUuids.size,
    totalNonFoil: cards.reduce((sum, c) => sum + c.quantity, 0),
    totalFoil: cards.reduce((sum, c) => sum + c.foilQuantity, 0),
  };
}
